import React from 'react';
import Text from '@root/components/v2/ui-elements/Text';
import { SEVERITY_COLORS, STATUS_COLORS, UI_COLORS } from '@root/utilities/pastelColors';
import DateFormatPipe from '@root/components/utilities/DateFormatPipe';
import { VulnerabilityCounts } from '@root/pages/kubernetes/pages/vulnerabilities/VulnerabilityCounts';

// ---- shared helpers / presentational bits (also consumed by the side overlay) ----

export const formatNumber = value => {
  if (value == null) return '-';
  return Number(value).toLocaleString();
};

// export const formatDuration = seconds => {
//   if (seconds == null) return '-';
//   const totalSeconds = Math.round(seconds);
//   if (totalSeconds < 60) return `${totalSeconds}s`;
//   if (totalSeconds < 3600) {
//     const mins = Math.floor(totalSeconds / 60);
//     const secs = totalSeconds % 60;
//     return secs ? `${mins}m ${secs.toString().padStart(2, '0')}s` : `${mins}m`;
//   }
//   const hours = Math.floor(totalSeconds / 3600);
//   const mins = Math.floor((totalSeconds % 3600) / 60);
//   return mins ? `${hours}h ${mins}m` : `${hours}h`;
// };

// Severity display metadata. Low is rendered neutral/gray to match the design (the palette's
// yellow "low" would otherwise collide with medium); critical/high/medium reuse SEVERITY_COLORS.
export const SEVERITY_META = [
  { key: 'critical', label: 'Critical', color: SEVERITY_COLORS.critical },
  { key: 'high', label: 'High', color: SEVERITY_COLORS.high },
  { key: 'medium', label: 'Medium', color: SEVERITY_COLORS.medium },
  { key: 'low', label: 'Low', color: '#94A3B8' },
];

export const StatusPill = ({ status }) => {
  const isCompleted = status !== 'errors';
  const color = isCompleted ? STATUS_COLORS.success : STATUS_COLORS.error;
  const label = isCompleted ? 'Completed' : 'Errors';
  return (
    <Text
      className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs"
      font="bold"
      element="span"
      style={{ borderColor: `${color}55`, backgroundColor: `${color}1A`, color }}>
      {/* <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: color, display: 'inline-block' }} /> */}
      {label}
    </Text>
  );
};

const CoverageBar = ({ scanned, total, isError }) => {
  const progress = total ? Math.min((scanned / total) * 100, 100) : 0;
  const color = isError ? STATUS_COLORS.error : progress === 100 ? STATUS_COLORS.success : STATUS_COLORS.pending;
  return (
    <div className="min-w-[150px] flex items-center gap-3">
      <div
        className="h-[7px] flex-1 overflow-hidden rounded-full"
        style={{ backgroundColor: UI_COLORS.background.tertiary }}>
        <div className="h-full rounded-full" style={{ width: `${progress}%`, backgroundColor: color }} />
      </div>
      <Text className="text-[13px] whitespace-nowrap" font="semi-bold" style={{ color: UI_COLORS.text.primary }}>
        {scanned} / {total}
      </Text>
    </div>
  );
};

// ---- table column definitions (order matches the design) ----

export const vulnerabilityScanColumns = [
  {
    field: 'cluster_name',
    headerName: 'Cluster',
    minWidth: 220,
    sortable: false,
    renderCell: params => (
      <div className="flex flex-col">
        <Text className="text-[13px]" font="bold" style={{ color: UI_COLORS.text.primary }}>
          {params.row.cluster_name ?? '-'}
        </Text>
        <Text className="text-[12px]" font="medium" style={{ color: UI_COLORS.text.secondary }}>
          {params.row.cluster_region ?? '-'}
        </Text>
      </div>
    ),
  },
  {
    field: 'namespace',
    headerName: 'Namespace',
    minWidth: 150,
    sortable: false,
    renderCell: params => (
      <Text className="text-[13px]" font="medium" style={{ color: UI_COLORS.text.secondary }}>
        {params.row.namespace ?? '-'}
      </Text>
    ),
  },
  {
    field: 'scan_status',
    headerName: 'Status',
    minWidth: 140,
    sortable: false,
    renderCell: params => <StatusPill status={params.row.scan_status} />,
  },
  {
    field: 'scanned',
    headerName: 'Workload Coverage',
    minWidth: 200,
    sortable: false,
    renderCell: params => (
      <CoverageBar
        scanned={params.row.coverage?.scanned ?? 0}
        total={params.row.coverage?.total_workloads ?? 0}
        isError={params.row.scan_status === 'errors'}
      />
    ),
  },
  {
    field: 'findings_total',
    headerName: 'Findings',
    minWidth: 110,
    sortable: false,
    renderCell: params => (
      <Text className="text-[13px]" font="bold" style={{ color: UI_COLORS.text.primary }}>
        {formatNumber(params.row.findings_total)}
      </Text>
    ),
  },
  {
    field: 'severity',
    headerName: 'Severity',
    minWidth: 320,
    sortable: false,
    renderCell: params => <VulnerabilityCounts vulnerabilities={params.row.severity} />,
  },
  {
    field: 'duration_display',
    headerName: 'Duration',
    minWidth: 110,
    sortable: false,
    renderCell: params => (
      <Text className="text-[13px]" font="medium" style={{ color: UI_COLORS.text.secondary }}>
        {params.row.duration_display}
      </Text>
    ),
  },
  {
    field: 'scan_timestamp',
    headerName: 'Scan Time',
    minWidth: 200,
    sortable: false,
    renderCell: params => DateFormatPipe(params.row.scan_timestamp),
  },
];
